const TURNSTILE_SCRIPT_ID = `cloudflare-turnstile-script`
const TURNSTILE_SCRIPT_URL = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit`
const TURNSTILE_LOAD_TIMEOUT_MS = 15000

export type TurnstileWidgetId = string
export type TurnstileTheme = `light` | `dark` | `auto`
export type TurnstileSize = `normal` | `compact`

export interface TurnstileRenderOptions {
  sitekey: string
  theme: TurnstileTheme
  size: TurnstileSize
  execution: `execute`
  appearance: `interaction-only`
  callback: (token: string) => void
  'error-callback': (errorCode: string) => boolean
  'expired-callback': () => void
  'timeout-callback': () => void
  'unsupported-callback': () => void
  'response-field': false
  retry: `never`
}

export interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: TurnstileRenderOptions
  ) => TurnstileWidgetId
  execute: (container: HTMLElement) => void
  reset: (widgetId: TurnstileWidgetId) => void
  remove: (widgetId: TurnstileWidgetId) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let turnstileScriptPromise: Promise<TurnstileApi> | undefined

export const loadTurnstile = (): Promise<TurnstileApi> => {
  if (typeof window === `undefined`) {
    return Promise.reject(new Error(`Turnstile is only available in a browser`))
  }

  if (window.turnstile) {
    return Promise.resolve(window.turnstile)
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise
  }

  turnstileScriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    let script = document.getElementById(
      TURNSTILE_SCRIPT_ID
    ) as HTMLScriptElement | null
    let settled = false

    const cleanup = () => {
      window.clearTimeout(timeoutId)
      script?.removeEventListener(`load`, handleLoad)
      script?.removeEventListener(`error`, handleError)
    }
    const fail = (error: Error) => {
      if (settled) return
      settled = true
      cleanup()
      script?.remove()
      reject(error)
    }
    const handleLoad = () => {
      if (settled) return
      if (window.turnstile) {
        settled = true
        cleanup()
        script?.setAttribute(`data-loaded`, `true`)
        resolve(window.turnstile)
      } else {
        fail(new Error(`Turnstile API did not initialize`))
      }
    }
    const handleError = () => {
      fail(new Error(`Turnstile script failed to load`))
    }

    if (script?.dataset.loaded === `true`) {
      script.remove()
      script = null
    }

    if (!script) {
      script = document.createElement(`script`)
      script.id = TURNSTILE_SCRIPT_ID
      script.src = TURNSTILE_SCRIPT_URL
      script.async = true
      script.defer = true
    }

    script.addEventListener(`load`, handleLoad, { once: true })
    script.addEventListener(`error`, handleError, { once: true })
    const timeoutId = window.setTimeout(
      () => fail(new Error(`Turnstile script timed out`)),
      TURNSTILE_LOAD_TIMEOUT_MS
    )

    if (!script.isConnected) {
      document.head.appendChild(script)
    }
  }).catch((error: Error) => {
    turnstileScriptPromise = undefined
    throw error
  })

  return turnstileScriptPromise
}
